import { FC, useState } from 'react'
import CustomDatePicker from './CustomDatePicker'
import { isNil } from 'lodash'
import moment from 'moment'
import { dateFormat } from 'app/appSettings'
import { Trans } from '@lingui/macro'

interface CustomDatePickerWithErrorProps {
  value: string | null
  error?: string
  helperText?: string
  disabled?: boolean
  onChange?: (e: string | null, keyboard?: string) => void
  style?: React.CSSProperties
  minDate?: string
  maxDate?: string
  inputVariant?: string
  fullWidth?: boolean
  format?: string
}

/**
 * This component is a wrapper for the CustomDatePicker component that adds error handling.
 * It validates the date format and checks if the date is within the minDate and maxDate range.
 * @param {CustomDatePickerWithErrorProps} props - The props for the CustomDatePicker component.
 * @returns {JSX.Element} The CustomDatePicker component with error handling.
 */
const CustomDatePickerWithError: FC<CustomDatePickerWithErrorProps> = ({ ...props }) => {
  const [error, setError] = useState<string | JSX.Element | null>(null)

  const isError = Boolean(!isNil(error) || props.error)

  const handleChange: (e: string | null) => void = (e) => {
    if (!isNil(props.onChange)) {
      props.onChange(e)
    }
    const currentDate = moment.utc(e, dateFormat, true).format(dateFormat)
    const minDate = !isNil(props.minDate) ? moment.utc(props.minDate, dateFormat, true).format(dateFormat) : null
    const maxDate = !isNil(props.maxDate) ? moment.utc(props.maxDate, dateFormat, true).format(dateFormat) : null
    if (!moment(e, dateFormat, true).isValid()) {
      setError(<Trans>VALID_DATE_FORMAT_ERROR</Trans>)
    } else if (!isNil(minDate) && currentDate < minDate) {
      setError(<Trans>FORM_DATE_PICKER_DATE_ON_OR_AFTER_ERROR {minDate}</Trans>)
    } else if (!isNil(maxDate) && currentDate > maxDate) {
      setError(<Trans>FORM_DATE_PICKER_DATE_ON_OR_BEFORE_ERROR {maxDate}</Trans>)
    } else {
      setError(null)
    }
  }

  return (
    <CustomDatePicker
      {...props}
      error={isError && !(props.disabled ?? false)}
      helperText={isError && !(props.disabled ?? false) ? (error ?? props.error) : props.helperText}
      onChange={handleChange}
    />
  )
}

export default CustomDatePickerWithError
